import { Controller } from "@hotwired/stimulus"

// Shows a "Turn on notifications" banner for users who have never enabled push notifications.
// The banner starts hidden (server renders it for users with no subscriptions) and this
// controller shows it if the browser supports notifications, permission isn't blocked,
// and the user hasn't dismissed it.
//
// Priority: defers to the PWA install banner and notifications-disabled banner.
// Only shows when no other banner is visible. Listens for banner:dismissed to try again.
// Hides itself when a higher-priority banner is shown later (e.g. PWA install on beforeinstallprompt).
export default class extends Controller {
  static targets = ["banner"]

  connect() {
    if (!this.shouldShow()) return

    this.boundBannerDismissed = () => this.onOtherBannerDismissed()
    this.boundBannerShown = (e) => this.onOtherBannerShown(e)
    document.addEventListener("banner:dismissed", this.boundBannerDismissed)
    document.addEventListener("banner:shown", this.boundBannerShown)

    // If a higher-priority banner (PWA install or notifications-disabled) is already visible, wait
    if (this.isOtherBannerVisible()) {
      return
    }
    this.show()
  }

  disconnect() {
    if (this.boundBannerDismissed) {
      document.removeEventListener("banner:dismissed", this.boundBannerDismissed)
    }
    if (this.boundBannerShown) {
      document.removeEventListener("banner:shown", this.boundBannerShown)
    }
  }

  shouldShow() {
    // Browser doesn't support notifications — no point showing
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return false

    // iOS Safari only supports Web Push in standalone (PWA) mode.
    // navigator.standalone is an iOS Safari-specific property:
    //   undefined → not iOS Safari
    //   false    → iOS Safari in regular browser (push won't work)
    //   true     → iOS Safari in standalone PWA (push works)
    if (window.navigator.standalone === false) return false

    // User explicitly blocked notifications — the "disabled" banner handles that case
    if (Notification.permission === "denied") return false

    // User dismissed this prompt before
    if (this.isDismissed()) return false

    return true
  }

  show() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove("hidden")
    }
  }

  dismiss() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden")
    }
    this.markDismissed()

    // Let other banners know they can show now
    document.dispatchEvent(new CustomEvent("banner:dismissed"))
  }

  onOtherBannerDismissed() {
    if (!this.isOtherBannerVisible()) {
      document.removeEventListener("banner:dismissed", this.boundBannerDismissed)
      this.boundBannerDismissed = null
      this.show()
    }
  }

  // Check if the PWA install banner or notifications-disabled banner is visible
  isOtherBannerVisible() {
    const selectors = [
      '[data-controller="pwa-install"] [data-pwa-install-target="banner"]:not(.hidden)',
      '[data-controller="push-subscription"] [data-push-subscription-target="browserDisabledBanner"]:not(.hidden)'
    ]
    return selectors.some(sel => document.querySelector(sel))
  }

  isDismissed() {
    try {
      return localStorage.getItem("enable_notifications_dismissed") === "true"
    } catch {
      return false
    }
  }

  markDismissed() {
    try {
      localStorage.setItem("enable_notifications_dismissed", "true")
    } catch {
      // localStorage unavailable
    }
  }
}
